import React, { useEffect, useState, useRef, useCallback, Suspense, lazy } from 'react';
import { setToken } from '../Upshow';
import styles from './App.module.scss';
import leftNavigation from '../assets/leftNavigation.svg';
import tvIcon from '../assets/tv.png';
import useOutsideClick from '../components/OutsideClickHandler';
import '../index.scss';
import moment from 'moment-timezone';
import Fuse from 'fuse.js';
import { eventsSearchKeys, fuseSearch } from '@upshow/live-events';
import SingleCard from '../v2/SingleCard';
import TermsPopup from '../v2/TermsPopup';
import SidePanel from '../v2/SidePanel';
import SportCard from '../v2/SportCard';
import { scrollToCardStart } from '../utils/scrollToCardStart';
import useEventsFilter from '../hooks/useEventsFilter';
import { normaliseKey } from '../utils/normalizeKeys';
import LeaguesCard from '../v2/LeaguesCard';
import EventsFilter from '../v2/EventsFilter';
import { isBackKey } from '../utils/keys';
import useArrowGrid from '../hooks/useArrowGrid';
import searchIcon from '../assets/searchEvents.svg';
import useKeyHold from '../hooks/useKeyHold';
import { EventStatusDictionary } from '../helpers/eventHelper';

const SearchResults = lazy(() => import('../v2/SearchResults'));
const LinkButton = lazy(()=> import('../v2/LinkButton'));
const MarketingButton = lazy(()=> import('../components/MarketingButton'));


/**
 * Searches through an array of event objects using a search algorithm.
 *
 * @function
 * @param {Object[]} events - The list of event objects to search through.
 * @param {string} searchTerm - The term to search for within the events.
 * @returns {Object[]} The list of matched event objects.
 *
 * @example
 * const events = [
 *   { ... },
 *   { ... },
 * ];
 *
 * const results = searchEvents(events, 'USL');
 */
export const searchEvents = (events, searchTerm) => fuseSearch(Fuse, events, eventsSearchKeys, searchTerm);

function getLocalTime(tz) {
    if (!tz) return null;
    return moment().tz(tz).format('hh:mm A');
}

export function handleMessage(event) {
    if (event.data?.type === "tvremote") {
        const customEvent = new CustomEvent("tvremote", {
            detail: { action: event.data?.key },
        });
        document.dispatchEvent(customEvent);
    }
}

const BASE_URL = process.env.REACT_APP_REST_URL;
const INACTIVITY_THRESHOLD_MS = 15000; // 15 seconds
const CARD_WIDTH = 386;
const CARD_GAP = 24;

export const HOLD_INTERVAL_MS = 300; // ← one move every 0.3 s
export const SCROLL_STEP = CARD_WIDTH + CARD_GAP;

const App = (
        {
            appState,
            onRequestOpen,
            onRequestClose,
            onHidden,
            open,
            setPollingPaused,
            eventsApi,
            epsOnly = false,
            onToggleSubscription,
            isLoading,
            isSubscribeLoading,
            containerRef,
            sportsData,
            shortcutsData,
            mounted,
            onShowSearchChange,
            openMenu,
            handleMarketingClick,
            isXumo,
            activeLiveEventId
        }) => {
    const [currentTime, setCurrentTime] = useState(new Date());
    const isOpen = open;
    const proxySetIsOpen = (val) => (val ? onRequestOpen() : onRequestClose());
    const [promotionActive, setPromotionActive] = useState(false);
    const [selectedIndex] = useState(0);
    const eventRefs = useRef([]);
    const marketingButtonRef = useRef(null);
    const searchButtonRef = useRef(null);
    const eventsWrapperRef = useRef(null);

    const [lastActivityTime, setLastActivityTime] = useState(Date.now());
    const [inactive, setInactive] = useState(false);

    const [showPopup, setShowPopup] = useState(false);
    const [showSidePanel, setShowSidePanel] = useState(false);
    const showSidePanelRef = useRef(showSidePanel);
    const [sideEvent, setSideEvent] = useState(null);
    const cardRefs = React.useRef({});
    const [fullScreen, setFullScreen] = useState(false);

    const categoriesWrapperRef = useRef(null);
    const rowWrapperRefs = useRef([]);
    const backBtnRef = useRef(null);
    const ctaRef = useRef(null);
    const lastFocusedRef = useRef(null);
    const EPG_OPEN_DELAY = isXumo ? 0 : 700;

    const [showSearch, setShowSearch] = useState(false);

    const getCardRef = useCallback((key, idx) => {
        if (!cardRefs.current[key]) cardRefs.current[key] = [];
        if (!cardRefs.current[key][idx]) cardRefs.current[key][idx] = React.createRef();
        return cardRefs.current[key][idx];
    }, []);

    const pollingPaused = inactive || showSearch || showSidePanel;
    useEffect(() => {
        setPollingPaused(pollingPaused);
    }, [pollingPaused, setPollingPaused]);

    const [menuReady, setMenuReady] = useState(true);

    useEffect(() => {
        if (onShowSearchChange) onShowSearchChange(showSearch);
    }, [showSearch, onShowSearchChange]);

 // NEW ─ guarda si alguna vez estuvo abierto
    const hasEverBeenOpen = useRef(false);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        if (open) {
            hasEverBeenOpen.current = true;
            return;
        }

        if (!hasEverBeenOpen.current) return;

        let fallback;
        if (!open) eventsApi.fetchEvents();

        const onStart = (e) => {
            if (e.target === el && e.propertyName === 'transform') {
                setMenuReady(false);
                fallback = setTimeout(() => {
                    setMenuReady(true);
                    onHidden();
                }, 600);
            }
        };

        const onEnd = (e) => {
            if (e.target === el && e.propertyName === 'transform') {
                clearTimeout(fallback);
                setMenuReady(true);
                onHidden();
            }
        };

        if(!isXumo) {
            el.addEventListener('transitionstart', onStart);
            el.addEventListener('transitionend', onEnd);
        }

        return () => {
            if(!isXumo) {
                clearTimeout(fallback);
                el.removeEventListener('transitionstart', onStart);
                el.removeEventListener('transitionend', onEnd);
            }
        };
    }, [open, onHidden]);

    const { liveEvents, upcomingEvents, scheduledEvents, completeLiveEvents } = eventsApi;

    const {
            showFiltered: showEventsFiltered,
            selectedSport,
            filteredEvents: filteredSportEvents,
            handleCardSelect: handleCardSelectFromFilter,
            resetFilter: handleBackFromEventsFiltered,
        } = useEventsFilter(completeLiveEvents, searchEvents);


    const listenerStart = (e) => {
        if (!isOpen && e.target === containerRef?.current) {
            // starts closing, tab disabled
            const focusableElements = containerRef?.current.querySelectorAll('*');
            focusableElements.forEach((el) => {
                el.tabIndex = -1;
            });
        }
    };

    const listenerEnd = (e) => {
        if (isOpen && e.target === containerRef?.current) {
            // ends oppening, tab enabled
            const focusableElements = containerRef?.current.querySelectorAll('*');
            focusableElements.forEach((el) => {
                el.tabIndex = undefined;
            });
            if (eventRefs.current[0]) {
                eventRefs.current[0].focus({ preventScroll: true });
            }
        }
    };

    useEffect(() => {
        containerRef?.current?.addEventListener('transitionstart', listenerStart);
        containerRef?.current?.addEventListener('transitionend', listenerEnd);
        return () => {
            containerRef?.current?.removeEventListener('transitionstart', listenerStart);
            containerRef?.current?.removeEventListener('transitionend', listenerEnd);
        };
    }, [isOpen]);

    const sortedEvents = React.useMemo(() => {
        if (!activeLiveEventId) return liveEvents;

        const nowPlayingIdx = liveEvents.findIndex((ev) => ev.id === activeLiveEventId);
        if (nowPlayingIdx === -1) return liveEvents;

        const nowPlayingEvent = liveEvents[nowPlayingIdx];
        const otherEvents = liveEvents.filter((_, i) => i !== nowPlayingIdx);

        return [nowPlayingEvent, ...otherEvents];
    }, [liveEvents, activeLiveEventId]);

    const categories = React.useMemo(
        () => [
            { key: 'live', title: 'Live', list: sortedEvents },
            { key: 'sports', title: 'Sports', list: sportsData },
            { key: 'leagues', title: 'Leagues', list: shortcutsData },
            { key: 'upcoming', title: 'Upcoming', list: upcomingEvents },
            { key: 'scheduled', title: 'Scheduled Events', list: scheduledEvents },
        ],
        [sortedEvents, upcomingEvents, scheduledEvents, sportsData, shortcutsData]
    );

    const focusCard = React.useCallback(
        ({ row, col }) => {
            let key = '';
            let node = null;

            //Focus first card on the next row if scheduled or upcoming become empty
            if(row > 2 && categories[row]?.list.length === 0) {
                key = row === 3 ? categories[row + 1]?.key : categories[row - 1]?.key;
                node = cardRefs.current[key]?.[0]?.current;
                setCursor({row: row === 3 ? 4 : 3, col: 0})
            }
            else {
                key = categories[row]?.key
                node = cardRefs.current[key]?.[col]?.current;
            }

            if (!node) return;
            if (!showSidePanelRef.current) {
                node.focus({ preventScroll: true });
                node.scrollIntoView({
                    behavior: 'auto',
                    inline: 'center',
                    block: 'center',
                });
            }
        },
        [categories]
    );

    //TODO-ANDROID
    const [isAndroidTV, setIsAndroidTV] = useState(false);

    useEffect(() => {
        // Detect if running in Android TV WebView
        if (window.AndroidTV && typeof window.AndroidTV.isAndroidTV === 'function') {
            const urlParams = new URLSearchParams(window.location.search);
            const apiToken = urlParams.get('api_token');
            setToken(apiToken);
            setIsAndroidTV(window.AndroidTV.isAndroidTV());
        }
    }, []);

    useEffect(() => {
        const handleUserActivity = () => {
            if (inactive) {
                setInactive(false);
            }
            // Update the last activity time
            setLastActivityTime(Date.now());
        };

        document.addEventListener('mousemove', handleUserActivity);
        document.addEventListener('keydown', handleUserActivity);
        document.addEventListener('click', handleUserActivity);
        document.addEventListener('touchstart', handleUserActivity);

        return () => {
            document.removeEventListener('mousemove', handleUserActivity);
            document.removeEventListener('keydown', handleUserActivity);
            document.removeEventListener('click', handleUserActivity);
            document.removeEventListener('touchstart', handleUserActivity);
        };
    }, [inactive]);

    useEffect(() => {
        const id = setInterval(() => {
            /* 0️⃣  Any overlay that should *freeze* the timer ─────────────────── */
            if (showPopup) return; //  ← optional: T&C popup immunity

            /* 1️⃣  Regular timeout behaviour ──────────────────────────────────── */
            if (Date.now() - lastActivityTime > INACTIVITY_THRESHOLD_MS && mounted) {
                setShowSidePanel(false);
                showSidePanelRef.current = false;
                onHidden();
                setInactive(true);

            }
        }, 1_000);

        return () => clearInterval(id);
    }, [
        showSearch, // ← keep effect up-to-date
        showSidePanel,
        showPopup,
        lastActivityTime,
        isOpen
    ]);

    const tz = appState?.settings?.tz ?? window.__UpshowState?.settings?.tz;

    useEffect(() => {
        const timerId = setInterval(() => {
            if (tz) {
                setCurrentTime(getLocalTime(tz));
            } else {
                setCurrentTime(new Date());
            }
        }, 60000);

        // Run immediately on mount
        if (tz) {
            setCurrentTime(getLocalTime(tz));
        } else {
            setCurrentTime(new Date());
        }

        return () => clearInterval(timerId);
    }, [tz]);

    const centerCard = ({ row, col }) => {
        const rowEl = rowWrapperRefs.current[row]?.current;
        if (!rowEl) return;

        scrollToCardStart(rowEl, col, CARD_WIDTH, CARD_GAP);
    };

    const centerRow = (rowIdx) => {
        const row = rowWrapperRefs.current[rowIdx]?.current;
        row?.scrollIntoView({ block: 'end', behavior: 'auto', inline: 'end' });
    };
    const getLastVisibleRow = useCallback(() => {
        for (let i = categories.length - 1; i >= 0; i--) {
            const cat = categories[i];

            if (cat.key === 'sports' || cat.key === 'leagues') {
                const visible = cat.list.filter((s) => completeLiveEvents.some((ev) => ev.information?.sport_type === s.id));
                if (visible.length >= 3) return i;
            } else if (cat.list.length) {
                return i;
            }
        }
        return 0;
    }, [categories, completeLiveEvents]);

    const lastVisibleRow = getLastVisibleRow();

    const activity = () => {
        setInactive(false);
        setLastActivityTime(Date.now());
    };

    const closeMenu = () => {
        setCursor({ row: -1, col: -1 }); // Reset navigation when closing menu
        setShowSidePanel(false);
        showSidePanelRef.current = false;
        onRequestClose();
    };

    const {
        cursor,
        handleKey: moveCursor,
        setCursor,
    } = useArrowGrid({
        setIsOpen: proxySetIsOpen,
        isOpen,
        rowStart: -1, // -1 is the header row
        rows: lastVisibleRow + 2, // header + every category row
        colsInRow: (r) => (r === -1 ? 2 : categories[r]?.list?.length || 0),
        onExitUp: () => {
            onRequestOpen();
        }, // nothing above header
        onActivity: activity,
        enabled: menuReady && !showSidePanel && !showEventsFiltered && !showSearch && !showPopup,
        onExitBack: closeMenu, // Close EPG on back key y resetea foco/selección
        isBackKey,
        resetColOnRowChange: true,
        isMainPanel: true,
        repeatDelay: 100,
    });

    useKeyHold('ArrowRight', () => moveCursor('ArrowRight'));
    useKeyHold('ArrowLeft', () => moveCursor('ArrowLeft'));
    useKeyHold('ArrowDown',  () => moveCursor('ArrowDown'));
    useKeyHold('ArrowUp',    () => moveCursor('ArrowUp'));

    useEffect(() => {
        if (!inactive && isOpen) {
            setFullScreen(false);
            const id = setTimeout(() => {
                setCursor({ row: 0, col: 0 });
                const firstRow = rowWrapperRefs.current[0]?.current;
                if(firstRow) firstRow.scrollLeft = 0;
                categoriesWrapperRef.current?.scrollTo({ top: 0, behavior: 'auto' });
            }, EPG_OPEN_DELAY);

            return () => clearTimeout(id);
        }
    }, [inactive, isOpen, mounted]);

    useEffect(() => {
        if (!epsOnly && !menuReady) return;
        if (showEventsFiltered || showSearch || showPopup) return;
        function handleKeyDown(event) {

            if (showSearch) return;
            const key = normaliseKey(event.key);
            setInactive(false);
            setLastActivityTime(Date.now());

            // If EPG is closed and user presses the back key → open it
            if (!isOpen && isBackKey(key)  && menuReady) {
                onRequestOpen();
                return;
            }

            // Prevent TAB from stealing focus in the WebView
            if (event.key === 'Tab') {
                event.preventDefault();
                return;
            }

            if (!showPopup) moveCursor(key);
        }

        function handleTvRemote(event) {
            const key = normaliseKey(event.detail?.action || event.detail?.key || event.key);
            if (showSearch) return;

            // Mark user activity
            setInactive(false);
            setLastActivityTime(Date.now());

            if (!showPopup) moveCursor(key);
        }

        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('tvremote', handleTvRemote);
        window.addEventListener('message', handleMessage);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('tvremote', handleTvRemote);
            window.removeEventListener('message', handleMessage);
        };
    }, [moveCursor, isOpen, showPopup, showEventsFiltered, showSearch]);

    useEffect(() => {
        if(cursor.row === 1){
            setFullScreen(true);
        }
        if (cursor.row === -1  && cursor.col >= 0 && !showSearch) {
            (cursor.col === 0 ? searchButtonRef : marketingButtonRef).current?.focus({ preventScroll: true });
            setFullScreen(false);
            return;
        } else if (!showEventsFiltered) {
            // Grid rows
            focusCard(cursor);
            centerCard(cursor);
            centerRow(cursor.row);
        }
    }, [cursor, focusCard]);

    useEffect(() => {
        showSidePanelRef.current = showSidePanel;
        if(!menuReady || showEventsFiltered || showSearch || showPopup) return;
        if(!showSidePanel) focusCard(cursor);

        const intercept = (e) => {
            const key = normaliseKey(e.key || e.detail?.action || e.detail?.key);
            if (showSidePanel) {
                if (key === 'ArrowUp') backBtnRef.current?.focus({ preventScroll: true });
                if (key === 'ArrowDown') ctaRef.current?.focus({ preventScroll: true });
                e.stopPropagation();
            }
        };

        document.addEventListener('message', intercept, true);
        document.addEventListener('keydown', intercept, true);
        document.addEventListener('tvremote', intercept, true);

        // Cleanup on unmount
        return () => {
            document.removeEventListener('tvremote', intercept, true);
            document.removeEventListener('keydown', intercept, true);
            document.removeEventListener('message', intercept, true);
        };
    }, [showSidePanel, showEventsFiltered, showSearch, showPopup]);

    // Focus the currently selected event
    useEffect(() => {
        if (eventRefs.current[selectedIndex]) {
            eventRefs.current[selectedIndex].focus({ preventScroll: true });
        }
    }, [selectedIndex]);

    // If there's no activeLiveEvent, show marketing
    useEffect(() => {
        if (appState?.activeLiveEvent === null) {
            setPromotionActive(true);
        } else {
            setPromotionActive(false);
        }
    }, [appState?.activeLiveEvent]);

    useOutsideClick(containerRef, () => proxySetIsOpen(false), (isOpen && !showSearch));


    const getEvent = (eventId) => {
        return [...upcomingEvents, ...scheduledEvents, ...liveEvents].find(upEvent => upEvent.id === eventId);
    }

    const handleCardSelect = (event) => {
        if (event.id === activeLiveEventId){
            requestAnimationFrame(() => {
                onRequestClose();
            });
        }

        if (event.id !== activeLiveEventId){
            setSideEvent(getEvent(event.id));
            setShowSidePanel(true);
        }
    };

    const handlePlayNow = async (event) => {
        if(!mounted) return;

        if (event.shouldSignTC) {
            lastFocusedRef.current = document.activeElement;
            setShowPopup(true);
            return;
        } else {
            await onToggleSubscription(getEvent(event.id));
            if(event.status === EventStatusDictionary.LIVE && event.id !== activeLiveEventId) {
                setShowSidePanel(false);
                showSidePanelRef.current = false;
            }
        }
    };


    const handleClosePopup = () => {
        setShowPopup(false);
        requestAnimationFrame(() => {
        lastFocusedRef.current?.focus({ preventScroll: true });
        });
    };


    useEffect(() => {
        const map = {};
        categories.forEach((cat) => {
            map[cat.key] = cat.list.map(() => React.createRef());
        });
        cardRefs.current = map;
    }, [categories]);

    // Focus selected card when side panel or events filter is closed
    useEffect(() => {
        if (!showSidePanel && !showEventsFiltered ) {
            if (cursor && cursor.row >= 0 && cursor.col >= 0) {
                const key = categories[cursor.row]?.key;
                const node = cardRefs.current[key]?.[cursor.col]?.current;
                if (node) {
                    node.focus({ preventScroll: true });
                    return;
                }
            }
            if (eventRefs.current[selectedIndex]) {
                eventRefs.current[selectedIndex].focus({ preventScroll: true });
            }
        }
    }, [showSidePanel, showEventsFiltered, cursor, categories, selectedIndex]);

    useEffect(() => {
        const containerEl = eventsWrapperRef.current;
        const cardEl = eventRefs.current[selectedIndex];

        if (!containerEl || !cardEl) return;

        cardEl.focus({ preventScroll: true });

        const containerRect = containerEl.getBoundingClientRect();
        const cardRect = cardEl.getBoundingClientRect();

        const cardOffsetLeft = cardRect.left - containerRect.left;

        const absoluteCardOffset = cardOffsetLeft + containerEl.scrollLeft;

        const centerOffset = absoluteCardOffset - containerRect.width / 2 + cardRect.width / 2;

        containerEl.scrollTo({
            left: centerOffset,
            behavior: 'smooth',
        });
    }, [selectedIndex]);

    useEffect(() => {
        const cardMap = {};
        const rowMap = [];

        categories.forEach((cat, rowIdx) => {
            // cards
            cardMap[cat.key] = cat.list.map(() => React.createRef());
            // row
            rowMap[rowIdx] = React.createRef();
        });

        cardRefs.current = cardMap;
        rowWrapperRefs.current = rowMap;
    }, [categories]);

    return (
        mounted && <>
            {showPopup && (
                <div className={styles.popUpTerms}>
                    <TermsPopup isOpen={showPopup} onClose={handleClosePopup} message={`To continue viewing this content, log in to the EverPass Manager via desktop, click on ${sideEvent.subscriptionType?.display_name} event and agree to the latest Terms and Conditions`} />
                </div>
            )}

            <SidePanel 
                isOpen={showSidePanel} 
                isLoading={isSubscribeLoading} 
                event={getEvent(sideEvent?.id)} 
                onClose={() => setShowSidePanel(false)} 
                onPlay={handlePlayNow} 
                ctaRef={ctaRef} 
                backBtnRef={backBtnRef} 
                deviceId={appState?.settings?.device_id}  
                isNowPlaying={sideEvent?.id === appState?.activeLiveEvent?.id} 
                tz={tz} 
            />

            {showSearch && (
                <Suspense fallback={<div>Loading...</div>}>
                    <SearchResults
                        liveEvents={liveEvents}
                        upcomingEvents={upcomingEvents}
                        scheduledEvents={scheduledEvents}
                        onClose={() => {
                            setShowSearch(false);
                            onRequestOpen();
                        }}
                        onToggleSubscription={onToggleSubscription}
                        currentTime={moment(currentTime, 'hh:mm A').format('h:mm A')}
                        activeEventId={activeLiveEventId}
                        handleCardSelectFromFilter={handleCardSelectFromFilter}
                        isLoading={isSubscribeLoading}
                        openMenu={openMenu}
                        onActivity={activity}
                        showPopup={showPopup}
                        setShowPopup={setShowPopup}
                        lastFocusedRef={lastFocusedRef}
                        setSideEventPopup={setSideEvent}
                    />
                </Suspense>

            )}

            {showEventsFiltered && (
                (() => {
                    return <EventsFilter
                                openMenu={openMenu}
                                events={filteredSportEvents()}
                                selectedSport={selectedSport}
                                onBack={handleBackFromEventsFiltered}
                                currentTime={moment(currentTime, 'hh:mm A').format('h:mm A')}
                                onToggleSubscription={onToggleSubscription}
                                activeEventId={activeLiveEventId}
                                tz={tz}
                                handleCardSelectFromFilter={handleCardSelectFromFilter}
                                isLoading={isSubscribeLoading}
                                onActivity={activity}
                                setShowPopup={setShowPopup}
                                showPopup={showPopup}
                                lastFocusedRef={lastFocusedRef}
                                setSideEventPopup={setSideEvent}
                                        />;
                })()
            ) }
            {
                <div ref={containerRef} className={`${styles.containerWrapper}  ${fullScreen ? styles.fullScreen : ''} ${isOpen ? styles.open : ''} ${(showPopup || showSearch) && styles.pointerEventNone}`}>
                    {/* Header */}
                    <div className={styles.headerInfo}>
                        <img src={leftNavigation} style={{ width: '156px' }} alt="nav" />
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '30px',
                            }}
                        >
                            {!isXumo && (
                                <Suspense fallback={<div>Loading...</div>}>
                                    <LinkButton
                                        ref={searchButtonRef}
                                        onClick={() => { setShowSearch(true) }}
                                        icon={<img src={searchIcon} alt="Search events" />}
                                    />
                                </Suspense>
                            )}
                            {!isXumo && (
                                <Suspense fallback={<div>Loading...</div>}>
                                    <MarketingButton
                                        ref={marketingButtonRef}
                                        isActive={promotionActive || activeLiveEventId === null}
                                        onClick={handleMarketingClick}
                                        icon={<img src={tvIcon} alt="TV icon" />}
                                    />
                                </Suspense>
                            )}
                            <h2>{tz && moment(currentTime, 'hh:mm A').format('h:mm A')}</h2>
                        </div>
                    </div>
                    {!isLoading && liveEvents.length === 0 && (
                        <div className={styles.noEventsWrapper}>
                            <p className={styles.noEvents}>There are no Live Events to display</p>
                        </div>
                    )}
                    <div ref={categoriesWrapperRef} className={styles.categoriesWrapper}>
                        {categories.map((cat, rowIdx) => {
                            if ((cat.key === 'leagues' || cat.key === 'sports') && cat.list.length < 3) {
                                return null;
                            }
                            const leagueTitleText = cat.key === 'leagues' ? 'Leagues, Conferences & Partners' : cat.title;
                            const categortyTitleText = cat.key === 'sports' ? 'All Sports' : leagueTitleText;

                            return (
                                <div key={cat.key} ref={rowWrapperRefs.current[rowIdx]} className={styles.rowGroup}>
                                    {cat?.list?.length > 0 ? <h2 className={cat.key === 'live' ? styles.firstCat : undefined}>{categortyTitleText}</h2> : null}
                                    <div className={styles.eventsWrapper}>
                                        {cat.key === 'sports' && cat.list.map((sport, colIdx) => <SportCard key={sport.id} ref={getCardRef(cat.key, colIdx)} sport={sport} onSelect={() => handleCardSelectFromFilter(sport, { type: 'sport' })} />)}
                                        {cat.key === 'leagues' && cat.list.map((league, colIdx) => <LeaguesCard key={league.id} ref={getCardRef(cat.key, colIdx)} league={league} onSelect={() => handleCardSelectFromFilter(league, { type: 'league' })} />)}
                                        {!(cat.key === 'sports' || cat.key === 'leagues') && cat.list.map((event, colIdx) => <SingleCard key={event.id} ref={getCardRef(cat.key, colIdx)} event={event} isNowPlaying={event.id === activeLiveEventId} onToggleSubscription={handleCardSelect} now={currentTime} tz={tz} />)}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                }

        </>
    );
};

App.displayName = 'MVPApp';
export default App;
